import { createReducer } from "reduxsauce"
import createActions from "helpers/actionCreator"
import { keysIn, omit, forEach, size } from "lodash"
import pluralize from "pluralize"
import { post } from "api"
import { log } from "helpers/logger"
import { takeLatest, put, call, select } from "redux-saga/effects"
import { Creators as modelCreators, Types as ModelTypes } from "model/data"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Types as WatchlistsMenuTypes } from "modules/watchlists-menu/model"
import { Types as WatchlistRootTypes } from "modules/watchlist-root/model"
import { getModelId, getSelectedDate } from "modules/watchlist-root/selectors"
import { computeSecurities } from "helpers/watchlists"
import { computeUpdatedFilters } from "helpers/filters"
import { getUserPreference } from "helpers/userPreferences"

export const { Types, Creators } = createActions("ompnt/modules/watchlist/", {
  toggleWatchlistsMenu: [],
  setPage: ["page"],
  setSelectedColumns: {
    props: ["columns", "modelId"],
    updatePreference: {
      getItems: (state, { columns, modelId }) => [{
        type: "watchlistSelectedColumns",
        key: modelId,
        value: columns,
      }],
    },
  },
  setSort: ["sort"],
  selectSecurity: ["security", "selected"],
  selectAllSecurities: ["securities", "selected"],
  removeFromWatchlist: ["watchlist", "securities"],
  removeFromWatchlistSuccess: [],
  removeFromWatchlistError: [],
  resetSelectedSecurities: [],
  toggleFilters: [],
  toggleColumnSelector: [],
  searchColumn: ["column", "search"],
  download: ["account", "modelId", "filter", "sort", "selectedColumns", "date"],
  updateFilters: {
    props: ["filter"],
    track: true,
    trackType: "watchlist-segment-filters-update",
    trackPayload: ({ filter }) => ({ filter }),
  },
})

const DEFAULT_STATE = {
  watchlistsMenuVisible: false,
  filter: null,
  selectedColumns: null,
  columnSelectorOpen: false,
  columnSearches: {},
  selectedSecurities: {
    total: 0,
    securities: {},
  },
  page: 0,
  filtersVisible: false,
  sort: null,
  editingFilter: null,
}

function handleToggleColumnSelector(state) {
  return { ...state, columnSelectorOpen: !state.columnSelectorOpen }
}

function handleSetSelectedColumns(state, { columns }) {
  return { ...state, selectedColumns: columns }
}

function handleSearchColumn(state, { column, search }) {
  const searches = { ...state.columnSearches }
  searches[column] = search
  return { ...state, columnSearches: searches }
}

function handleToggleWatchlistsMenu(state) {
  return { ...state, watchlistsMenuVisible: !state.watchlistsMenuVisible }
}

function handleSetPage(state, { page }) {
  return { ...state, page }
}

function handleSetSort(state, { sort }) {
  return { ...state, sort }
}

function handleSelectSecurity(state, { security }) {
  const selectedSecurities = { securities: { ...state.selectedSecurities.securities } }

  if (selectedSecurities.securities[security.id]) {
    selectedSecurities.securities = omit(selectedSecurities.securities, security.id)
  } else {
    selectedSecurities.securities[security.id] = {
      assetClass: security.assetClass,
      ticker: security.ticker,
      id: security.id,
      mic: security.mic,
      modelProviderId: security.modelProviderId,
      internalId: security.internalId,
    }
  }

  selectedSecurities.total = keysIn(selectedSecurities.securities).length

  return { ...state, selectedSecurities }
}

function handleSelectAllSecurities(state, { securities, selected }) {
  const selectedSecurities = { securities: { ...state.selectedSecurities.securities } }

  forEach(securities, security => {
    if (!selected) {
      selectedSecurities.securities = omit(selectedSecurities.securities, security.id)
    } else if (!selectedSecurities.securities[security.id]) {
      selectedSecurities.securities[security.id] = {
        assetClass: security.assetClass,
        ticker: security.ticker,
        id: security.id,
        mic: security.mic,
        modelProviderId: security.modelProviderId,
        internalId: security.internalId,
      }
    }
  })

  selectedSecurities.total = keysIn(selectedSecurities.securities).length

  return { ...state, selectedSecurities }
}

function handleRemoveFromWatchlist(state) {
  return { ...state }
}

function handleRemoveFromWatchlistError(state) {
  return { ...state }
}

function handleRemoveFromWatchlistSuccess(state) {
  return {
    ...state,
    selectedSecurities: {
      total: 0,
      securities: {},
    },
  }
}

function handleResetSelectedSecurities(state) {
  return {
    ...state,
    selectedSecurities: {
      total: 0,
      securities: {},
    },
  }
}

function handleToggleFilters(state) {
  return { ...state, filtersVisible: !state.filtersVisible }
}

function handleUpdateFilters(state, { filter }) {
  return { ...state, ...computeUpdatedFilters(filter) }
}

function handleLoadSuccess(state, action) {
  if (action.resource === "watchlistPreferences") {
    const selectedColumns = getUserPreference(action.data, "watchlistSelectedColumns")
    return { ...state, selectedColumns }
  }
  return state
}

export default createReducer(DEFAULT_STATE, {
  [Types.SEARCH_COLUMN]: handleSearchColumn,
  [Types.TOGGLE_COLUMN_SELECTOR]: handleToggleColumnSelector,
  [Types.SET_PAGE]: handleSetPage,
  [Types.SET_SELECTED_COLUMNS]: handleSetSelectedColumns,
  [Types.SET_SORT]: handleSetSort,
  [Types.SELECT_SECURITY]: handleSelectSecurity,
  [Types.SELECT_ALL_SECURITIES]: handleSelectAllSecurities,
  [Types.REMOVE_FROM_WATCHLIST]: handleRemoveFromWatchlist,
  [Types.REMOVE_FROM_WATCHLIST_SUCCESS]: handleRemoveFromWatchlistSuccess,
  [Types.REMOVE_FROM_WATCHLIST_ERROR]: handleRemoveFromWatchlistError,
  [Types.RESET_SELECTED_SECURITIES]: handleResetSelectedSecurities,
  [WatchlistRootTypes.SET_DATE]: handleResetSelectedSecurities,
  [WatchlistsMenuTypes.ADD_TO_WATCHLISTS_SUCCESS]: handleResetSelectedSecurities,
  [Types.TOGGLE_FILTERS]: handleToggleFilters,
  [Types.UPDATE_FILTERS]: handleUpdateFilters,
  [Types.TOGGLE_WATCHLISTS_MENU]: handleToggleWatchlistsMenu,
  [ModelTypes.LOAD_SUCCESS]: handleLoadSuccess,
})

function* handleDownload({ account, modelId, filter, sort, selectedColumns, date }) {
  try {
    yield call(post, `/api/${account}/m/${modelId}/download/search`, { filter, sort, selectedColumns, date, fileName: "watchlist_securities" })
  } catch (err) {
    log(err)
  }
}

function* removeFromWatchlist({ watchlist, securities }) {
  try {
    const { account, modelId, selectedDate } = yield select(state => ({
      account: state.global.selected.account,
      modelId: getModelId(state),
      selectedDate: getSelectedDate(state),
    }))

    const { successWatchlistsIds } = yield call(post, `/api/${account}/watchlists/removeSecurities`, { modelId, date: selectedDate, securities: computeSecurities(securities.securities), watchlistIds: [watchlist] })
    if (size(successWatchlistsIds)) {
      yield put(Creators.removeFromWatchlistSuccess())
      yield put(modelCreators.resetCollections(["watchlistSecurities", "accountWatchlists"]))
      yield put(notificationCreators.add("success", null, `${pluralize("Security", securities.total)} removed from the watchlist.`))
    } else {
      yield put(Creators.removeFromWatchlistError())
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while removing securities from your watchlist."))
    }
  } catch {
    yield put(Creators.removeFromWatchlistError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while removing securities from your watchlist."))
  }
}

export function* saga() {
  yield takeLatest(Types.REMOVE_FROM_WATCHLIST, removeFromWatchlist)
  yield takeLatest(Types.DOWNLOAD, handleDownload)
}
